import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchLandingSettings,
  updateLandingSettings,
  type LandingModule,
  type LandingSettings,
} from '../../../api/landing.api';

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all bg-white';

const emptyForm = (): LandingSettings => ({
  company_name: 'DressnMore',
  phone: '',
  whatsapp: '',
  email: '',
  address: '',
  working_hours: '',
  facebook_url: '',
  instagram_url: '',
  twitter_url: '',
  linkedin_url: '',
  tiktok_url: '',
  youtube_url: '',
  footer_copyright: '',
  modules: [],
});

function Field({ label, desc, children }: { label: string; desc?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      {desc && <p className="text-xs text-gray-400 mb-1.5">{desc}</p>}
      {children}
    </div>
  );
}

export default function LandingSection() {
  const { t } = useTranslation();
  const [form, setForm] = useState<LandingSettings>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const r = await fetchLandingSettings();
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.message || t('settings.landing.load_error'));
        return;
      }
      setForm({ ...emptyForm(), ...r.data, modules: r.data.modules ?? [] });
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const set = (key: keyof LandingSettings, val: string) =>
    setForm((p) => ({ ...p, [key]: val }));

  const setModule = (index: number, patch: Partial<LandingModule>) => {
    setForm((p) => ({
      ...p,
      modules: p.modules.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    }));
  };

  const moveModule = (index: number, dir: -1 | 1) => {
    setForm((p) => {
      const next = [...p.modules];
      const target = index + dir;
      if (target < 0 || target >= next.length) return p;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return { ...p, modules: next };
    });
  };

  const removeModule = (index: number) => {
    setForm((p) => ({ ...p, modules: p.modules.filter((_, i) => i !== index) }));
  };

  const addModule = () => {
    setForm((p) => ({
      ...p,
      modules: [...p.modules, { icon: 'ri-apps-line', label: '' }],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    const r = await updateLandingSettings({
      company_name: form.company_name,
      phone: form.phone,
      whatsapp: form.whatsapp,
      email: form.email,
      address: form.address,
      working_hours: form.working_hours,
      facebook_url: form.facebook_url,
      instagram_url: form.instagram_url,
      twitter_url: form.twitter_url,
      linkedin_url: form.linkedin_url,
      tiktok_url: form.tiktok_url,
      youtube_url: form.youtube_url,
      footer_copyright: form.footer_copyright,
      modules: form.modules.filter((m) => m.label.trim() !== ''),
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.message || t('settings.landing.save_error'));
      return;
    }
    setForm({ ...emptyForm(), ...r.data, modules: r.data.modules ?? [] });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl ring-1 ring-gray-100 px-6 py-16 text-center text-sm text-gray-400">
        {t('settings.landing.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-rose-50 rounded-xl border border-rose-100 text-sm text-rose-600">
          <i className="ri-error-warning-line" />
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-50">
            <i className="ri-home-gear-line text-sky-600 text-lg" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('settings.landing.contact_title')}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{t('settings.landing.contact_subtitle')}</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t('settings.landing.company_name')}>
              <input className={inputCls} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} />
            </Field>
            <Field label={t('settings.landing.email')}>
              <input type="email" dir="ltr" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Field>
            <Field label={t('settings.landing.phone')} desc={t('settings.landing.phone_desc')}>
              <input dir="ltr" className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field label={t('settings.landing.whatsapp')} desc={t('settings.landing.whatsapp_desc')}>
              <input dir="ltr" className={inputCls} value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
            </Field>
            <Field label={t('settings.landing.address')}>
              <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} />
            </Field>
            <Field label={t('settings.landing.working_hours')}>
              <input className={inputCls} value={form.working_hours} onChange={(e) => set('working_hours', e.target.value)} />
            </Field>
            <Field label={t('settings.landing.footer_copyright')} desc={t('settings.landing.footer_copyright_desc')}>
              <input className={inputCls} value={form.footer_copyright} onChange={(e) => set('footer_copyright', e.target.value)} />
            </Field>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-violet-50">
            <i className="ri-share-line text-violet-600 text-lg" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('settings.landing.social_title')}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{t('settings.landing.social_subtitle')}</p>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {(
            [
              ['facebook_url', 'ri-facebook-fill'],
              ['instagram_url', 'ri-instagram-line'],
              ['twitter_url', 'ri-twitter-x-line'],
              ['linkedin_url', 'ri-linkedin-fill'],
              ['tiktok_url', 'ri-tiktok-line'],
              ['youtube_url', 'ri-youtube-line'],
            ] as Array<[keyof LandingSettings, string]>
          ).map(([key, icon]) => (
            <Field key={key} label={t(`settings.landing.${key}`)}>
              <div className="relative">
                <i className={`${icon} absolute start-3 top-1/2 -translate-y-1/2 text-gray-400`} />
                <input
                  dir="ltr"
                  className={`${inputCls} ps-9`}
                  placeholder="https://"
                  value={String(form[key] ?? '')}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            </Field>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50">
              <i className="ri-apps-line text-blue-600 text-lg" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">{t('settings.landing.modules_title')}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{t('settings.landing.modules_subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={addModule}
            className="px-3 py-2 text-sm font-semibold text-teal-700 bg-teal-50 rounded-xl hover:bg-teal-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line me-1" />
            {t('settings.landing.add_module')}
          </button>
        </div>
        <div className="p-6 space-y-3">
          {form.modules.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">{t('settings.landing.no_modules')}</p>
          )}
          {form.modules.map((mod, index) => (
            <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white ring-1 ring-gray-100 text-blue-600 flex-shrink-0">
                <i className={`${mod.icon || 'ri-apps-line'} text-lg`} />
              </div>
              <input
                className={`${inputCls} max-w-[180px]`}
                value={mod.icon}
                onChange={(e) => setModule(index, { icon: e.target.value })}
                placeholder="ri-apps-line"
                dir="ltr"
              />
              <input
                className={`${inputCls} flex-1`}
                value={mod.label}
                onChange={(e) => setModule(index, { label: e.target.value })}
                placeholder={t('settings.landing.module_label')}
              />
              <div className="flex items-center gap-1 flex-shrink-0">
                <button type="button" onClick={() => moveModule(index, -1)} className="w-8 h-8 rounded-lg text-gray-400 hover:bg-white cursor-pointer" aria-label="up">
                  <i className="ri-arrow-up-s-line" />
                </button>
                <button type="button" onClick={() => moveModule(index, 1)} className="w-8 h-8 rounded-lg text-gray-400 hover:bg-white cursor-pointer" aria-label="down">
                  <i className="ri-arrow-down-s-line" />
                </button>
                <button type="button" onClick={() => removeModule(index)} className="w-8 h-8 rounded-lg text-rose-400 hover:bg-rose-50 cursor-pointer" aria-label="delete">
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
            <i className="ri-checkbox-circle-fill" />
            {t('settings.saved')}
          </span>
        )}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {saving ? t('settings.landing.saving') : t('settings.save')}
        </button>
      </div>
    </div>
  );
}
